// ─────────────────────────────────────────────
//  CrnaRosterPanel — the day's CRNAs / AAs with their go-home time.
//  Right-rail companion to MdCallPanel. Read-only reflection of today's
//  roster (the people you selected to work that day), sorted soonest-to-leave.
//  Props:
//    crnas — rostered CRNAs/AAs enriched with subRole + goHomeHour/shiftCode.
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../theme/tokens";
import Card from "./Card";
import { sortCrnasByGoHome, goHomeHourToClock, shiftCodeEndHour24 } from "../domain/schedule";

const { color, font, space, radius } = tokens;

const roleTag = (subRole) => (subRole || "crna").toUpperCase().slice(0, 3);

// On-call 24h shift: code "24", or an am/pm range with identical ends ("7a-7a",
// "11p-11p") → covers a full 24h (7 AM to 7 AM next day).
const isOnCallShift = (code) => {
  const c = String(code || "").trim().toLowerCase();
  if (c === "24") return true;
  const m = c.match(/^(\d{1,2}[ap])-(\d{1,2}[ap])$/);
  return Boolean(m && m[1] === m[2]);
};

// Departure clock for a roster member: prefer the per-day shift code (what the
// grid sets), fall back to the legacy numeric goHomeHour. Null when unset.
const departureLabel = (c) => {
  if (c.shiftCode) {
    const h = shiftCodeEndHour24(c.shiftCode);
    return h == null ? null : `${String(h).padStart(2, "0")}:00`;
  }
  if (c.goHomeHour != null) return goHomeHourToClock(c.goHomeHour).label;
  return null;
};

// Group rostered CRNAs/AAs by their shift end time, preserving the
// soonest-to-leave sort order so groups come out 15:00 → 17:00 → 19:00 → 21:00,
// then an "On call · 24 hrs" group, then any truly-unscheduled members in a
// trailing "No shift time" group.
function groupByDeparture(listed) {
  const groups = [];
  listed.forEach((c) => {
    const onCall = isOnCallShift(c.shiftCode);
    const time = onCall ? null : departureLabel(c); // "15:00" | null
    const key = onCall ? "__oncall__" : time || "__none__";
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = {
        key,
        time,
        onCall,
        label: onCall ? "On call · 24 hrs" : time || "No shift time",
        members: [],
      };
      groups.push(g);
    }
    g.members.push(c);
  });
  // Order: timed groups (by sort) first, then on-call, then no-shift — stable.
  const rank = (g) => (g.onCall ? 1 : g.key === "__none__" ? 2 : 0);
  return groups
    .map((g, i) => ({ ...g, _i: i }))
    .sort((a, b) => rank(a) - rank(b) || a._i - b._i);
}

export default function CrnaRosterPanel({ crnas = [] }) {
  const listed = sortCrnasByGoHome(crnas);
  const groups = groupByDeparture(listed);

  return (
    <Card style={st.card}>
      <h2 style={st.heading}>CRNA &amp; AA Roster</h2>

      {listed.length === 0 ? (
        <p style={st.empty}>No CRNAs or AAs on today's roster.</p>
      ) : (
        groups.map((g) => (
          <section key={g.key} style={st.group} aria-label={g.label}>
            {/* Group header doubles as the divider between shift end-time groups. */}
            <h3 style={{ ...st.groupHeader, ...(g.onCall ? st.groupHeaderOnCall : {}) }}>
              <span>{g.label}</span>
              <span style={st.groupCount}>({g.members.length})</span>
            </h3>
            <ul style={st.list} aria-label={g.onCall ? g.label : `Providers leaving at ${g.time || "an unset time"}`}>
              {g.members.map((c) => {
                const time = departureLabel(c);
                return (
                  <li key={c.id} style={st.row}>
                    <span style={st.tag} aria-hidden="true">{roleTag(c.subRole)}</span>
                    <span style={st.name}>{c.name}</span>
                    <span style={st.time}>{g.onCall ? "7a–7a" : time ? `→ ${time}` : "—"}</span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </Card>
  );
}

const st = {
  card: { fontFamily: font.family },
  heading: {
    margin: `0 0 ${space.md}px`,
    fontSize: font.size.heading,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    lineHeight: 1.2,
  },
  list: { listStyle: "none", margin: 0, padding: 0 },
  group: { marginBottom: space.xs },
  // Shift end-time divider/header — mirrors the Daily grid section header
  // (raised pill, bold, secondary) so it reads as one of the page's group bars.
  groupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    margin: `${space.sm}px 0 ${space.xs}px`,
    padding: `${space.xs}px ${space.sm}px`,
    background: color.surface.raised,
    border: `1px solid ${color.border.default}`,
    borderRadius: 6,
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    letterSpacing: "0.5px",
  },
  groupHeaderOnCall: { background: "#ffe6ad", borderColor: "#f4d488", color: "#9a6712" },
  groupCount: { color: color.text.muted, fontWeight: font.weight.bold },
  row: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    padding: `${space.xs}px 0`,
    borderBottom: `1px solid ${color.border.default}`,
    minHeight: 36,
  },
  tag: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 34,
    height: 20,
    padding: "0 6px",
    borderRadius: radius.sm,
    background: color.surface.raised,
    border: `1px solid ${color.border.default}`,
    color: color.text.secondary,
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: "0.5px",
    flexShrink: 0,
  },
  name: { flex: 1, fontSize: font.size.body, color: color.text.primary, minWidth: 0 },
  time: {
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.accent.primary,
    flexShrink: 0,
  },
  empty: { textAlign: "center", fontSize: font.size.body, color: color.text.muted, margin: `${space.md}px 0` },
};
